/* eslint-disable prettier/prettier */
import AssessmentIcon from '@mui/icons-material/Assessment'
import { Box, CircularProgress, Typography, Paper } from '@mui/material'

import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TemplateService from '../../services/templateService'
import { nowUnix } from '../../utils/datetime'
import { templateStats } from '../../helpers/reports'
import InlineLoading from '../../components/shared/InlineLoading'
import CommLogsCard from './CommLogsCard'
import CommsSentCard from './CommsSentCard'
import { channelMeta } from '../PipelineWizard/wizardShared'
import {
    ACCENT,
    BRAND,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'

const RANGE_DAYS = { '7d': 7, '30d': 30, '90d': 90 }

// The header row in the overline slot on the sunken well, like the theme's
// table heads.
const HEAD_SX = {
    typography: 'overline',
    color: TEXT.faint,
    px: 2,
    py: 1.25,
    borderBottom: `1px solid ${SURFACE.border}`,
    background: SURFACE.sunken,
}
const CELL_SX = {
    typography: 'body2',
    color: TEXT.secondary,
    px: 2,
    py: 1.25,
    borderBottom: `1px solid ${SURFACE.border}`,
}

const RateTag = ({ rate }) => {
    const tone =
        rate >= 70 ? STATUS.success : rate >= 40 ? STATUS.warning : STATUS.error
    return (
        <Box
            sx={{
                display: 'inline-block',
                px: 0.75,
                py: 0.25,
                borderRadius: `${RADIUS.pill}px`,
                background: tone.bg,
                color: tone.fg,
                typography: 'caption',
                fontWeight: 700,
            }}
        >
            {rate}%
        </Box>
    )
}

RateTag.propTypes = { rate: PropTypes.number.isRequired }

const Reports = () => {
    const dispatch = useDispatch()
    const { templates } = useSelector((state) => state.template)
    const [range, setRange] = useState('7d')
    // Until the templates arrive (with none from an earlier page in the
    // store), the stats would read 0 everywhere: show that they are loading.
    const [loading, setLoading] = useState(true)
    const pending = loading && !templates.length

    useEffect(() => {
        dispatch(TemplateService.getTemplates()).then(() => setLoading(false))
    }, [])

    // Stats are scoped to templates created within the selected range —
    // template objects carry `created_on` (unix seconds).
    const stats = useMemo(() => {
        const base = templateStats(templates, nowUnix(), RANGE_DAYS[range])
        return {
            ...base,
            byChannel: base.byChannel.map((c) => ({
                ...c,
                meta: channelMeta(c.key),
            })),
        }
    }, [templates, range])

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 3, md: 4 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <Box
                            sx={{
                                background: GRADIENT.brand,
                                borderRadius: `${RADIUS.md}px`,
                                p: 1,
                                display: 'flex',
                            }}
                        >
                            {/* Deep Blue on the orange-to-yellow tile in
                                both themes (white is 3.3:1 on orange). */}
                            <AssessmentIcon
                                sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{ color: TEXT.primary }}
                            >
                                Reports
                            </Typography>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{ color: TEXT.muted }}
                            >
                                Template stats, comm logs and comms sent
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {Object.keys(RANGE_DAYS).map((r) => (
                            <Box
                                key={r}
                                component="button"
                                onClick={() => setRange(r)}
                                aria-pressed={range === r}
                                sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: `${RADIUS.pill}px`,
                                    cursor: 'pointer',
                                    typography: 'caption',
                                    fontWeight: 600,
                                    background:
                                        range === r
                                            ? ACCENT.bg15
                                            : 'transparent',
                                    color:
                                        range === r ? ACCENT.text : TEXT.faint,
                                    border:
                                        range === r
                                            ? `1px solid ${ACCENT.border30}`
                                            : '1px solid transparent',
                                    transition: 'all 0.15s',
                                    '&:hover': { color: ACCENT.text },
                                }}
                            >
                                {r}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Summary cards */}
                <Box
                    sx={{
                        display: 'grid',
                        // Two by two on a phone; four in a row pushed the
                        // page past the screen.
                        gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    {[
                        {
                            label: 'Total Templates',
                            value: stats.total,
                            color: TEXT.primary,
                        },
                        {
                            label: 'Approved',
                            value: stats.approved,
                            color: STATUS.success.fg,
                        },
                        {
                            label: 'Pending Review',
                            value: stats.pending,
                            color: STATUS.warning.fg,
                        },
                        {
                            label: 'Rejected',
                            value: stats.disapproved,
                            color: STATUS.error.fg,
                        },
                    ].map((s) => (
                        <Paper
                            key={s.label}
                            elevation={0}
                            sx={{ ...cardSx, p: 2.5 }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: TEXT.faint,
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                {s.label}
                            </Typography>
                            {pending ? (
                                <CircularProgress
                                    size={22}
                                    aria-label={`Loading ${s.label}`}
                                    sx={{ color: BRAND.orange, my: '5px' }}
                                />
                            ) : (
                                <Typography
                                    variant="h1"
                                    component="p"
                                    sx={{
                                        color: s.color,
                                        lineHeight: 1,
                                    }}
                                >
                                    {s.value}
                                </Typography>
                            )}
                        </Paper>
                    ))}
                </Box>

                {/* Templates by Channel table */}
                <Paper
                    elevation={0}
                    sx={{ ...cardSx, mb: 3, overflow: 'hidden' }}
                >
                    <Typography
                        variant="overline"
                        component="h2"
                        sx={{
                            color: TEXT.muted,
                            display: 'block',
                            px: 2.5,
                            pt: 1.5,
                        }}
                    >
                        Templates by Channel
                    </Typography>
                    {/* Six columns need about 560px; narrower, the table
                        scrolls inside its card instead of widening the page. */}
                    <Box
                        data-testid="channel-table-scroll"
                        sx={{ overflowX: 'auto' }}
                    >
                        <Box sx={{ minWidth: 560 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        '2fr 1fr 1fr 1fr 1fr 1fr',
                                    ...HEAD_SX,
                                }}
                            >
                                {[
                                    'Channel',
                                    'Total',
                                    'Approved',
                                    'Pending',
                                    'Rejected',
                                    'Rate',
                                ].map((h) => (
                                    <Box key={h}>{h}</Box>
                                ))}
                            </Box>
                            {pending && (
                                <InlineLoading label="Loading template stats…" />
                            )}
                            {!pending &&
                                stats.byChannel.map(
                                    ({
                                        key,
                                        meta,
                                        total: t,
                                        approved: a,
                                        pending: p,
                                        disapproved: d,
                                    }) => {
                                        const rate =
                                            t > 0
                                                ? Math.round((a / t) * 100)
                                                : 0
                                        return (
                                            <Box
                                                key={key}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                        '2fr 1fr 1fr 1fr 1fr 1fr',
                                                    '&:last-child > *': {
                                                        borderBottom: 'none',
                                                    },
                                                    '&:hover': {
                                                        background:
                                                            SURFACE.cardHover,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        ...CELL_SX,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        color: TEXT.muted,
                                                    }}
                                                >
                                                    {meta.icon} {meta.label}
                                                </Box>
                                                <Box sx={CELL_SX}>{t}</Box>
                                                <Box
                                                    sx={{
                                                        ...CELL_SX,
                                                        color:
                                                            a > 0
                                                                ? STATUS.success
                                                                      .fg
                                                                : TEXT.faint,
                                                        fontWeight:
                                                            a > 0 ? 600 : 400,
                                                    }}
                                                >
                                                    {a}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ...CELL_SX,
                                                        color:
                                                            p > 0
                                                                ? STATUS.warning
                                                                      .fg
                                                                : TEXT.faint,
                                                    }}
                                                >
                                                    {p}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ...CELL_SX,
                                                        color:
                                                            d > 0
                                                                ? STATUS.error
                                                                      .fg
                                                                : TEXT.faint,
                                                    }}
                                                >
                                                    {d}
                                                </Box>
                                                <Box sx={CELL_SX}>
                                                    {t === 0 ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: TEXT.faint,
                                                            }}
                                                        >
                                                            —
                                                        </Typography>
                                                    ) : (
                                                        <RateTag rate={rate} />
                                                    )}
                                                </Box>
                                            </Box>
                                        )
                                    }
                                )}
                        </Box>
                    </Box>
                </Paper>

                <CommLogsCard />
                <CommsSentCard />
            </Box>
        </Box>
    )
}

export default Reports
